import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSchedule } from '../api';
import type { EnrichedMatch } from '../types';
import { rigaDate, rigaTime, stageLabel } from '../format';

interface Column { stage: string; label: string; slots: number; matches: EnrichedMatch[] }

const STAGE_DEF: { stage: string; label: string; slots: number }[] = [
  { stage: 'r32', label: 'R32', slots: 16 },
  { stage: 'r16', label: 'R16', slots: 8 },
  { stage: 'qf', label: 'QF', slots: 4 },
  { stage: 'sf', label: 'SF', slots: 2 },
  { stage: 'final', label: 'Final', slots: 1 },
];

export default function KnockoutBracket() {
  const { data, isLoading } = useQuery({
    queryKey: ['schedule'],
    queryFn: getSchedule,
    refetchInterval: 60_000,
  });

  const columns = useMemo<Column[]>(() => {
    const byStage = new Map<string, EnrichedMatch[]>();
    for (const m of data?.matches ?? []) {
      if (!STAGE_DEF.some((s) => s.stage === m.stage)) continue;
      const arr = byStage.get(m.stage) ?? [];
      arr.push(m);
      byStage.set(m.stage, arr);
    }
    for (const arr of byStage.values()) arr.sort((a, b) => a.utc_kickoff.localeCompare(b.utc_kickoff));
    return STAGE_DEF.map((s) => ({ ...s, matches: byStage.get(s.stage) ?? [] }));
  }, [data]);

  const thirdPlace = useMemo(
    () => (data?.matches ?? []).find((m) => m.stage === 'third') ?? null,
    [data],
  );

  if (isLoading) return <section className="card p-4 text-xs text-dim">Loading bracket…</section>;
  if (columns.every((c) => c.matches.length === 0)) {
    return (
      <section className="card p-4 text-center text-sm text-dim">
        Knockout fixtures will appear once the group stage ends.
      </section>
    );
  }

  // Each R32 cell takes 1 unit. R16=2, QF=4, SF=8, Final=16. So with `flex: 1` per "unit",
  // a column with N cells stacks 16/N units per cell and centers naturally.
  const CELL_UNITS: Record<string, number> = { r32: 1, r16: 2, qf: 4, sf: 8, final: 16 };

  return (
    <section className="card p-3 sm:p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold">Bracket</h2>
        <span className="text-[10px] text-dim">scroll horizontally if needed →</span>
      </div>

      <div className="overflow-x-auto -mx-1 px-1 pb-1">
        <div className="flex gap-3 min-w-[820px]" style={{ minHeight: 32 * 64 }}>
          {columns.map((col) => (
            <div key={col.stage} className="flex flex-col w-[160px] sm:w-[180px] shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-2 text-center">{col.label}</div>
              <div className="flex-1 flex flex-col gap-2">
                {Array.from({ length: col.slots }).map((_, slotIdx) => {
                  const m = col.matches[slotIdx];
                  const units = CELL_UNITS[col.stage] ?? 1;
                  return (
                    <div key={slotIdx} className="flex items-center" style={{ flex: units }}>
                      {m ? <MatchCard m={m} compact /> : <EmptySlot stage={col.stage} />}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {thirdPlace && (
        <div className="mt-4 pt-3 border-t border-ink-700">
          <div className="text-[10px] uppercase tracking-widest text-dim mb-2">Third-place play-off</div>
          <div className="max-w-[260px]">
            <MatchCard m={thirdPlace} compact />
          </div>
        </div>
      )}
    </section>
  );
}

function EmptySlot({ stage }: { stage: string }) {
  return (
    <div className="w-full rounded-lg bg-ink-800/30 ring-1 ring-ink-700/40 p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-dim">{stageLabel(stage)}</div>
      <div className="text-[10px] text-dim/60 mt-0.5">TBD</div>
    </div>
  );
}

function MatchCard({ m, compact }: { m: EnrichedMatch; compact?: boolean }) {
  const live = m.status === 'in';
  const done = m.status === 'post';
  const stuck = done && m.winner === null;

  let resultLabel = '';
  if (live) resultLabel = m.status_detail ?? 'LIVE';
  else if (stuck) resultLabel = 'ET/PK';
  else if (done) {
    resultLabel = m.ended_phase === 'ot' ? 'AET' : m.ended_phase === 'pk' ? 'Pens.' : 'FT';
  }

  const koTime = `${rigaDate(m.utc_kickoff).slice(0, 6)} ${rigaTime(m.utc_kickoff)}`;

  return (
    <Link
      to={`/matches/${m.id}`}
      className={`block w-full rounded-lg p-1.5 ring-1 transition-colors ${
        live ? 'bg-pitch-500/10 ring-pitch-500/40'
        : stuck ? 'bg-amber-500/5 ring-amber-500/30'
        : done ? 'bg-ink-800/60 ring-ink-700 hover:ring-ink-600'
        : 'bg-ink-800/40 ring-ink-700/60 hover:ring-ink-600'
      }`}
    >
      <div className="flex items-baseline justify-between mb-1 px-0.5">
        <span className={`text-[9px] font-mono ${
          live ? 'text-pitch-400 font-bold'
          : stuck ? 'text-amber-400'
          : 'text-dim'
        }`}>
          {live || done ? resultLabel : koTime}
        </span>
      </div>
      <TeamRow
        flag={m.home_flag ?? null} name={m.home_name} owner={m.home_owner ?? null}
        score={done || live ? m.home_score : null}
        pkScore={m.ended_phase === 'pk' ? m.home_pk_score ?? null : null}
        won={m.winner === 'home'}
        lost={done && m.winner === 'away'}
        tbd={!m.home_team}
        compact={compact}
      />
      <TeamRow
        flag={m.away_flag ?? null} name={m.away_name} owner={m.away_owner ?? null}
        score={done || live ? m.away_score : null}
        pkScore={m.ended_phase === 'pk' ? m.away_pk_score ?? null : null}
        won={m.winner === 'away'}
        lost={done && m.winner === 'home'}
        tbd={!m.away_team}
        compact={compact}
      />
    </Link>
  );
}

function TeamRow({ flag, name, owner, score, pkScore, won, lost, tbd, compact }: {
  flag: string | null; name: string; owner: string | null;
  score: number | null; pkScore: number | null;
  won: boolean; lost: boolean; tbd: boolean;
  compact?: boolean;
}) {
  return (
    <div className={`flex items-center gap-1.5 ${compact ? 'py-0' : 'py-0.5'} ${lost ? 'opacity-50' : ''}`}>
      <span className="text-sm shrink-0">{tbd ? '⏳' : (flag ?? '🏳️')}</span>
      <span className={`flex-1 min-w-0 truncate text-[11px] ${won ? 'font-semibold text-pitch-400' : ''}`}>
        {tbd ? 'TBD' : name}
      </span>
      {owner && (
        <span className="text-[8px] uppercase tracking-wider text-dim bg-ink-700/60 px-1 rounded shrink-0">
          {owner}
        </span>
      )}
      {score !== null && (
        <span className="stat shrink-0 tabular-nums text-xs">
          {score}
          {pkScore !== null && <span className="text-[9px] text-dim ml-0.5">({pkScore})</span>}
        </span>
      )}
    </div>
  );
}
